import React, { useState } from 'react';
import { trackLead } from '../utils/tracking';
import styled from 'styled-components';
import { FiMapPin, FiPhone, FiMail, FiClock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { api } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { WhatsAppService } from '../utils/whatsapp';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { useSettings } from '../contexts/SettingsContext';
import Seo, { organizationSchema } from '../components/Seo';
import { publicCoordinates } from '../utils/publicCoordinates';
import {
  Container, Section, Eyebrow, Button, Field, Label, Input, Textarea,
} from '../components/ui';

const Head = styled.div`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 2.5rem;

  h1 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: clamp(2rem, 4vw, 2.75rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.colors.foreground};
    margin: 0.5rem 0 0.75rem;
  }
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 1.05rem;
    line-height: 1.6;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 2rem;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 1.75rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const Alert = styled.div<{ $type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.8rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.92rem;
  font-weight: 500;
  ${({ $type }) =>
    $type === 'success'
      ? `background:#dcfce7;color:#15803d;`
      : `background:#fee2e2;color:#b91c1c;`}
`;

const InfoItem = styled.div`
  display: flex;
  gap: 0.9rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child { border-bottom: none; }

  .ic {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.colors.primarySoft};
    color: ${({ theme }) => theme.colors.primaryDark};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .lb { font-size: 0.78rem; color: ${({ theme }) => theme.colors.text.secondary}; }
  .vl {
    color: ${({ theme }) => theme.colors.foreground};
    font-weight: 500;
    white-space: pre-line;
    line-height: 1.5;
  }
`;

const MapBox = styled.div`
  height: 320px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  margin-top: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.card};

  iframe { width: 100%; height: 100%; border: none; }
`;

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { siteSettings } = useSettings();

  useScrollToTop();

  const handleInputChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      await api.post('/leads/contact', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        interest_type: 'information',
        notes: `${formData.subject}\n\n${formData.message}`,
        additional_info: { subject: formData.subject },
      });
      trackLead('contact_form');
      setSuccessMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
      const leadData = {
        name: formData.name, email: formData.email, phone: formData.phone,
        message: `${formData.subject}\n\n${formData.message}`,
      };
      setTimeout(() => WhatsAppService.showWhatsAppOption(leadData), 1000);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      setErrorMessage('Erro ao enviar mensagem. Tente novamente ou ligue para nós.');
    } finally {
      setLoading(false);
    }
  };

  const contactTitle = siteSettings?.contact_title || 'Entre em contato';
  const contactSubtitle =
    siteSettings?.contact_subtitle ||
    'Estamos aqui para ajudar você a encontrar o imóvel perfeito. Fale com a gente.';
  const address = siteSettings?.contact_address_full || (siteSettings as any)?.address || '';
  const phone = (siteSettings as any)?.phone || siteSettings?.company_info?.phone || '';
  const email = (siteSettings as any)?.email || siteSettings?.company_info?.email || '';
  const hours = siteSettings?.contact_hours ?? '';

  const mapEnabled = siteSettings?.contact_map_enabled !== false;
  const mapCoordinates = publicCoordinates(siteSettings?.contact_map_latitude, siteSettings?.contact_map_longitude);
  const googleMapsApiKey = siteSettings?.google_maps_api_key;

  const renderMap = () => {
    if (!mapEnabled || !mapCoordinates || !googleMapsApiKey) return null;
    const [mapLat, mapLng] = mapCoordinates;
    return (
      <MapBox>
        <iframe
          title="Mapa"
          src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${mapLat},${mapLng}&zoom=15`}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </MapBox>
    );
  };

  return (
    <div>
      <Seo
        title={`Contato | ${siteSettings?.company_info?.name || siteSettings?.site_name || 'Imobiliária'}`}
        description={contactSubtitle.slice(0, 158)}
        canonicalPath="/contato"
        structuredData={organizationSchema(siteSettings)}
      />
      <Header />
      <WhatsAppButton />

      <Section>
        <Container>
          <Head>
            <Eyebrow>Contato</Eyebrow>
            <h1>{contactTitle}</h1>
            <p>{contactSubtitle}</p>
          </Head>

          <Grid>
            <Card>
              <Form onSubmit={handleSubmit}>
                {successMessage && (
                  <Alert $type="success">
                    <FiCheckCircle size={18} /> {successMessage}
                  </Alert>
                )}
                {errorMessage && (
                  <Alert $type="error">
                    <FiAlertCircle size={18} /> {errorMessage}
                  </Alert>
                )}
                <Row>
                  <Field>
                    <Label>Nome *</Label>
                    <Input type="text" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} required />
                  </Field>
                  <Field>
                    <Label>Telefone</Label>
                    <Input type="tel" value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} placeholder="(00) 00000-0000" />
                  </Field>
                </Row>
                <Field>
                  <Label>E-mail *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} required />
                </Field>
                <Field>
                  <Label>Assunto *</Label>
                  <Input type="text" value={formData.subject} onChange={(e) => handleInputChange('subject', e.target.value)} required />
                </Field>
                <Field>
                  <Label>Mensagem *</Label>
                  <Textarea value={formData.message} onChange={(e) => handleInputChange('message', e.target.value)} required placeholder="Como podemos ajudar você?" />
                </Field>
                <Button type="submit" $variant="primary" $full disabled={loading}>
                  {loading ? 'Enviando...' : 'Enviar mensagem'}
                </Button>
              </Form>
            </Card>

            <div>
              <Card>
                {address && (
                  <InfoItem>
                    <span className="ic"><FiMapPin size={18} /></span>
                    <div><div className="lb">Endereço</div><div className="vl">{address}</div></div>
                  </InfoItem>
                )}
                {phone && (
                  <InfoItem>
                    <span className="ic"><FiPhone size={18} /></span>
                    <div><div className="lb">Telefone</div><div className="vl">{phone}</div></div>
                  </InfoItem>
                )}
                {email && (
                  <InfoItem>
                    <span className="ic"><FiMail size={18} /></span>
                    <div><div className="lb">E-mail</div><div className="vl">{email}</div></div>
                  </InfoItem>
                )}
                {hours && (
                  <InfoItem>
                    <span className="ic"><FiClock size={18} /></span>
                    <div><div className="lb">Horário</div><div className="vl">{hours}</div></div>
                  </InfoItem>
                )}
              </Card>
              {renderMap()}
            </div>
          </Grid>
        </Container>
      </Section>

      <Footer />
    </div>
  );
};

export default ContactPage;
